/**
 * Colour tokens (art §2.1 pigments, art §2.11 CSS names) and `applyTokens()` (architecture §3.10). Owner: UI/Visual (F3).
 *
 * `src/content/palette.ts` (F2) is the single colour source; {@link PIGMENTS} re-exports its `TOKENS`. `src/ui/tokens.css`
 * holds the same values for first paint (a unit test asserts the two agree with `tokensCss()`).
 */
import { TOKENS } from '../content/palette';

/** Art §2.1, every pigment (camelCase token → hex) — F2's `TOKENS` from `src/content/palette.ts` (the single source). */
export const PIGMENTS = TOKENS;

export type PigmentId = keyof typeof PIGMENTS;
export type Palette = Readonly<Record<PigmentId, string>>;

/** Art §2.11 also spells a few tokens differently from the §2.1 id (`copperLeaf` → `--mc-copper`): emitted as aliases. */
const CSS_ALIASES: Partial<Record<PigmentId, string>> = { copperLeaf: '--mc-copper' };

/** `inkDeep` → `--mc-ink-deep`. */
export function cssVarOf(id: PigmentId): string {
  return `--mc-${id.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`;
}

/** Non-colour art tokens (art §2.11). */
export const ART_SCALARS = { '--mc-line': '1.5px', '--mc-radius': '4px', '--mc-tap': '48px' } as const;

/** Every `--mc-*` declaration of the first-paint token file, as a name → value map. */
export function tokenDeclarations(palette: Palette = PIGMENTS): Record<string, string> {
  const out: Record<string, string> = {};
  for (const id of Object.keys(PIGMENTS) as PigmentId[]) {
    out[cssVarOf(id)] = palette[id];
    const alias = CSS_ALIASES[id];
    if (alias) out[alias] = palette[id];
  }
  return { ...out, ...ART_SCALARS };
}

/** Writes every pigment (and the art scalars) as custom properties on `root` (boot step, architecture §2.2). */
export function applyTokens(root: HTMLElement = document.documentElement, palette: Palette = PIGMENTS): void {
  for (const [k, v] of Object.entries(tokenDeclarations(palette))) root.style.setProperty(k, v);
}

/** Reads a token back from the computed style (TS never duplicates token values, ui-ux §17.1). */
export function readToken(name: string, el: Element = document.documentElement): string {
  return getComputedStyle(el).getPropertyValue(name).trim();
}

/** `#RRGGBB` → [r, g, b] in 0..255. */
export function hexToRgb(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** WCAG 2.x relative luminance of a `#RRGGBB` colour. */
export function luminance(hex: string): number {
  const lin = (c: number): number => { const s = c / 255; return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4; };
  const [r, g, b] = hexToRgb(hex);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** WCAG contrast ratio of two `#RRGGBB` colours. */
export function contrast(a: string, b: string): number {
  const la = luminance(a), lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
